'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const inputVariants = cva(
  'flex w-full border ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
  {
    variants: {
      variant: {
        default: 'border-input bg-background',
        outline: 'border-input bg-transparent',
        filled: 'border-transparent bg-secondary',
        ghost: 'border-transparent bg-transparent hover:bg-accent',
      },
      inputSize: {
        default: 'h-10 px-3 py-2 text-base md:text-sm',
        sm: 'h-9 px-3 py-2 text-sm',
        lg: 'h-11 px-4 py-2 text-base',
        xl: 'h-14 px-4 py-3 text-lg',
      },
      rounded: {
        default: 'rounded-md',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        full: 'rounded-full',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
      rounded: 'default',
    },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  floatingLabel?: boolean;
  label?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      variant,
      inputSize,
      rounded,
      floatingLabel,
      label,
      onFocus,
      onBlur,
      onChange,
      value,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(() => {
      // Inicializa com o valor correto
      return !!(value ?? defaultValue);
    });

    React.useImperativeHandle(ref, () => inputRef.current!);

    // Sincroniza hasValue quando value muda (controlled component)
    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(!!value);
      }
    }, [value]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Usa requestAnimationFrame para garantir que o valor está atualizado
      requestAnimationFrame(() => {
        if (inputRef.current) {
          setHasValue(!!inputRef.current.value);
        }
      });
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Atualiza imediatamente
      setHasValue(!!e.target.value);
      onChange?.(e);
    };

    if (!floatingLabel) {
      return (
        <input
          type={type}
          className={cn(
            inputVariants({ variant, inputSize, rounded, className }),
          )}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          {...props}
        />
      );
    }

    const isLabelUp = isFocused || hasValue;

    return (
      <div className="relative w-full">
        <input
          type={type}
          className={cn(
            inputVariants({ variant, inputSize, rounded }),
            className,
          )}
          ref={inputRef}
          value={value}
          defaultValue={defaultValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          {...props}
        />
        {label && (
          <label
            className={cn(
              'absolute left-3 pointer-events-none transition-all duration-200',
              isLabelUp && 'px-1 bg-background',
              !isLabelUp &&
                'top-1/2 -translate-y-1/2 text-lg text-muted-foreground',
              isLabelUp && '-top-3 text-sm',
              isFocused ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
